import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { SectionHead } from "@/pages/Home";
import {
  ArrowRight, Lightbulb, Compass, FileText, CheckSquare, Bot, Layout, Rocket,
} from "lucide-react";

const INCLUDED = [
  { icon: Layout, title: "ATF Business Starter Template", desc: "One flexible, professional single-page website starter—ready to adapt for a business, church, nonprofit, veteran initiative, community program, or personal brand. Preview it live before you commit.", cta: { label: "Preview the template", to: "/marketplace/demo/modern-business" } },
  { icon: CheckSquare, title: "Starter Launch Checklist", desc: "A plain-language checklist of the first practical steps to get from idea to a live, credible presence—so you always know the next move." },
  { icon: FileText, title: "Simple Planning Guide", desc: "A gentle, guided way to think through your idea: who you help, the problem you solve, and where to begin. No blank forms, no jargon." },
  { icon: Bot, title: "Top Foster — Your AI Business Strategist", desc: "Guidance that helps you think it through, one clear step at a time. Honest, AI-powered, and always in your corner." },
];

const STEPS = [
  { n: 1, icon: Lightbulb, title: "Tell us your idea", desc: "Share what you're trying to build and who you want to help. No wrong answers." },
  { n: 2, icon: Bot, title: "Meet Top Foster", desc: "Your AI Business Strategist asks a few thoughtful questions and points you to the right next step." },
  { n: 3, icon: Compass, title: "Get your starting point", desc: "See the ATF Business Starter Template and a simple plan shaped around your mission." },
  { n: 4, icon: Rocket, title: "Take the next step", desc: "Move forward with confidence—launch, manage, and grow when you're ready." },
];

export default function StarterKit() {
  return (
    <div data-testid="starter-kit-page">
      {/* HERO */}
      <section className="relative py-28 md:py-36 border-b border-white/5 grain">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-3xl">
            <div className="flex items-center gap-2 text-gold text-xs uppercase tracking-[0.25em] mb-6">
              <span className="h-px w-10 bg-gold" /> Business Starter Kit · Free first step
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight font-light text-white leading-[1.08]">
              Have an idea? <span className="text-gold">Let's build it together.</span>
            </h1>
            <p className="text-lg text-gray-300 font-light mt-8 leading-relaxed">
              Many people have a dream or a mission but aren't sure where to begin. You don't have to
              figure it out alone. Start with a few simple questions, get a flexible starter template,
              and let Top Foster guide your next step—no pressure, no credit card.
            </p>
            <div className="flex flex-wrap gap-4 mt-10">
              <Link to="/start" data-testid="starter-primary-cta" className="inline-flex items-center gap-2 bg-gold text-navy px-9 py-4 font-semibold hover:bg-gold-light transition-colors">
                Start with your idea <ArrowRight size={18} />
              </Link>
              <Link to="/marketplace/demo/modern-business" data-testid="starter-preview-cta" className="inline-flex items-center gap-2 border border-white/25 text-white px-9 py-4 font-medium hover:bg-white/5 transition-colors">
                Preview the starter template
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section className="py-20 md:py-28 max-w-7xl mx-auto px-6 md:px-12">
        <SectionHead label="Your free first step" title="What's in the Business Starter Kit" />
        <div className="grid sm:grid-cols-2 gap-6 mt-14" data-testid="starter-included">
          {INCLUDED.map((item, i) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: i * 0.08 }}
              className="group glass p-8 h-full hover:border-gold/40 transition-colors flex flex-col"
            >
              <div className="w-12 h-12 border border-gold/40 flex items-center justify-center text-gold mb-6 group-hover:scale-110 transition-transform duration-300">
                <item.icon size={22} strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-xl text-white mb-3 leading-snug">{item.title}</h3>
              <p className="text-gray-400 text-sm leading-relaxed flex-1">{item.desc}</p>
              {item.cta && (
                <Link to={item.cta.to} data-testid="starter-template-preview-link" className="inline-flex items-center gap-2 text-gold text-sm font-medium mt-5 hover:gap-3 transition-all">
                  {item.cta.label} <ArrowRight size={15} />
                </Link>
              )}
            </motion.div>
          ))}
        </div>
      </section>

      {/* TOP FOSTER EXPLAINER */}
      <section className="py-16 md:py-24 bg-navy-800/40 border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-2 flex lg:justify-center">
            <div className="w-16 h-16 border border-gold/40 flex items-center justify-center text-gold">
              <Bot size={28} strokeWidth={1.5} />
            </div>
          </div>
          <div className="lg:col-span-10">
            <SectionHead label="Guidance, not a chatbot" title="Meet Top Foster — Your AI Business Strategist" />
            <p className="text-gray-300 text-lg font-light leading-relaxed max-w-3xl mt-6" data-testid="starter-topfoster-copy">
              Not sure where to begin? Top Foster helps you think it through. Ask what you're trying to
              build, who you want to help, and the problem you're solving—and get a clear, honest next
              step, one at a time. Top Foster is AI-powered guidance designed to feel like a strategist
              in your corner, not a chatbot. It won't overwhelm you with jargon or tech—just practical
              direction toward your mission.
            </p>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 md:py-28 max-w-7xl mx-auto px-6 md:px-12">
        <SectionHead label="Simple by design" title="How it works" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-14" data-testid="starter-steps">
          {STEPS.map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: i * 0.08 }}
              className="relative border border-white/10 bg-navy-800/40 p-7 h-full"
            >
              <div className="flex items-center gap-3 mb-4">
                <span className="font-mono text-sm text-gold">0{s.n}</span>
                <div className="w-10 h-10 border border-gold/40 flex items-center justify-center text-gold">
                  <s.icon size={18} strokeWidth={1.5} />
                </div>
              </div>
              <h3 className="font-heading text-lg text-white mb-2 leading-snug">{s.title}</h3>
              <p className="text-gray-400 text-sm leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="pb-28 max-w-7xl mx-auto px-6 md:px-12">
        <div className="relative overflow-hidden bg-gradient-to-br from-navy-700 to-navy-800 border border-gold/20 p-12 md:p-16 text-center grain">
          <h2 className="font-heading text-3xl sm:text-4xl text-white tracking-tight mb-4">Your idea deserves a first step.</h2>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto mb-9">Start free with a few simple questions. We'll help you turn what's in your head into something real.</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/start" data-testid="starter-cta-start" className="inline-flex items-center gap-2 bg-gold text-navy px-9 py-4 font-semibold hover:bg-gold-light transition-colors">
              Start with your idea <ArrowRight size={18} />
            </Link>
            <Link to="/register" data-testid="starter-cta-signup" className="inline-flex items-center gap-2 border border-white/25 text-white px-9 py-4 font-medium hover:bg-white/5 transition-colors">
              Create a free account
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
